import * as z from "zod"
import { isAtLeast18 } from "@/lib/age"

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, "E-mail é obrigatório")
    .email("E-mail inválido"),
  password: z
    .string()
    .min(1, "Senha é obrigatória")
    .min(8, "Senha deve ter no mínimo 8 caracteres")
    .regex(/[A-Z]/, "Senha deve conter pelo menos uma letra maiúscula")
    .regex(/[a-z]/, "Senha deve conter pelo menos uma letra minúscula")
    .regex(/[0-9]/, "Senha deve conter pelo menos um número")
    .regex(/[^A-Za-z0-9]/, "Senha deve conter pelo menos um caractere especial"),
})

export const loginLinkSchema = z.object({
  email: z
    .string()
    .min(1, "E-mail é obrigatório")
    .email("E-mail inválido"),
})

export const registerSchema = z.object({
  name: z
    .string()
    .min(3, "Preencha o nome completo")
    .max(100, "Nome muito longo")
    .regex(/^[\p{L}\p{M}'\s]+$/u, "Preencha o nome completo")
    .refine((v) => /[\p{L}]/u.test(v), "Preencha o nome completo"),
  email: z
    .string()
    .min(1, "E-mail é obrigatório")
    .email("E-mail inválido")
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "E-mail inválido"),
  birthDate: z
    .coerce.date({
      required_error: "Informe sua data de nascimento",
      invalid_type_error: "Data de nascimento inválida",
    })
    .min(new Date(1900, 0, 1), "Data de nascimento inválida")
    .max(new Date(), "Data de nascimento não pode estar no futuro")
    .refine((d) => isAtLeast18(d), "Você precisa ter pelo menos 18 anos"),
  gender: z.enum(["MASCULINO", "FEMININO"], {
    required_error: "Selecione o gênero",
  }),
  acceptTerms: z
    .boolean()
    .refine((val) => val === true, "Você precisa aceitar os termos de uso"),
})

export const resetPasswordSchema = z.object({
  password: z
    .string()
    .min(1, "Senha é obrigatória")
    .min(8, "Senha deve ter no mínimo 8 caracteres")
    .regex(/[A-Z]/, "Senha deve conter pelo menos uma letra maiúscula")
    .regex(/[a-z]/, "Senha deve conter pelo menos uma letra minúscula")
    .regex(/[0-9]/, "Senha deve conter pelo menos um número")
    .regex(/[^A-Za-z0-9]/, "Senha deve conter pelo menos um caractere especial"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

export const completeProfileSchema = z.object({
  gender: z.enum(["MASCULINO", "FEMININO"], {
    required_error: "Selecione o gênero",
  }),
  birthDate: z.preprocess(
    (val) => {
      if (!val || val === "") return undefined;
      if (val instanceof Date && !isNaN(val.getTime())) return val;
      return undefined;
    },
    z.date({
      required_error: "A data de nascimento é obrigatória",
      invalid_type_error: "Data de nascimento inválida",
    })
    .min(new Date(1900, 0, 1), "Data de nascimento inválida")
    .max(new Date(), "Você veio do futuro? Informe uma data de nascimento válida")
    .refine((d) => isAtLeast18(d), "Você precisa ter pelo menos 18 anos")
  ),
});

export type LoginInput = z.infer<typeof loginSchema>
export type LoginLinkInput = z.infer<typeof loginLinkSchema> 
export type RegisterInput = z.infer<typeof registerSchema>
export type CompleteProfileInput = z.infer<typeof completeProfileSchema>
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>